import React, { useState } from 'react';

import { Row, Col, notification } from 'antd';
import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { datePickerToApi, getNow } from '@libs/NwMoment';
import { getMoment } from '@libs/NwMoment';
import { getProjectDescriptionsOptions, getProjectTitlesOptions } from '@libs/ProjectUtils';

import { useProjectContext } from '@contexts/ProjectContext';
import { useStateValue } from '@contexts/StateProvider';

import { forceReloadProject, onGetOrderedEventList } from '@hooks/API/Project/UseProject';
import { forceReloadProjectAccountingDetails } from '@hooks/API/Project/UseProjectAccountingDetails';
import { forceReloadProjectStatements } from '@hooks/API/Project/UseProjectStatements';
import { forceReloadProjectTransactions } from '@hooks/API/Project/UseProjectTransactions';
import useTransactionLabels from '@hooks/CachedLists/UseTransactionLabels';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { Loading } from '@components/Gui/Loading';
import {
    NWAutoComplete,
    NWTextAreaAutoComplete,
    NWNonBookingEventSelectorForTransactions,
    NWDatePicker,
} from '@components/Gui/NwForm/NwFormItems';
import { NwForm } from '@components/Gui/NwForm/NwFormWrapper';
import { ProjectTransactionFormContainer } from '@components/Project/ProjectTransactions/Transactions/ProjectTransactionsUI';
import {
    handleChangeEventForDate,
    getProjectModelsForTransactions,
    getEventModelsForTransactions,
} from '@components/Project/ProjectTransactions/Transactions/TransactionCreateFormUtils';
import ModelSelectorProject from '@components/Transactions/TransactionsCreateForms/Common/ModelSelectorProject';

import AmountLine from './Common/AmountLine';
import NwFixedCancelAndSaveBar from '@components/Gui/NwFixedCancelAndSaveBar';
import ServiceLine from './Common/ServiceLine';
import TransactionLabelsSelector from './Common/TransactionLabelsSelector';

const ExpenseFormProject = ({ onCloseDrawer, clientBillingInfos, contacts, width, minWidth }) => {
    //onChangeForm

    const contextData = useProjectContext();
    const { projectData: currentProject, transactionsGrouped } = contextData;

    const transactionTitles = [
        ...new Set(
            transactionsGrouped.flatMap((group) => group.Dates.flatMap((date) => date.Transactions.map((t) => t.Title)))
        ),
    ].filter(Boolean);

    const transactionDescriptions = [
        ...new Set(
            transactionsGrouped.flatMap((group) =>
                group.Dates.flatMap((date) => date.Transactions.map((t) => t.Description))
            )
        ),
    ].filter(Boolean);

    const transactionProperties = useTransactionLabels('Expense');
    const getTransactionLabel = (transactionLabelID) =>
        transactionProperties.labels.find((label) => label.ID === transactionLabelID);
    const defaultTypeQuantity = 'Units';

    const configurations = useConfigurations();

    const [{ areaContext }] = useStateValue();
    const events = onGetOrderedEventList(currentProject, areaContext, false, false);

    const [groupedModelDates, setGroupedModelDates] = useState(getProjectModelsForTransactions(currentProject));

    const saveTransaction = async (values, setSubmitting) => {
        //TODO: validazione: se c'è evento ci deve essere modella

        let apiData = {
            StatementType: 'Model',
            TransactionLabelID: values.TransactionLabelID,
            Title: values.Title,
            Description: values.Description,
            Quantity: values.Quantity[0],
            TypeQuantity: values.Quantity[1],
            Currency: values.Amount[0],
            SingleFee: values.Amount[1],
            RefDate: values.RefDate && datePickerToApi(values.RefDate),
        };
        if (values.ServiceID) {
            apiData.ServiceID = values.ServiceID;
            if (values.AgencyCost) {
                apiData.AgencyCost = values.AgencyCost[1];
            } else {
                apiData.AgencyCost = values.SingleFee[1];
            }
        }

        let models = [];
        for (const groupedModel of groupedModelDates) {
            if (groupedModel.Active) {
                const modelData = {
                    ContactID: groupedModel.ModelID,
                    ContactBillingInfoID: null,
                    ProjectID: currentProject.ID,
                };
                if (values.SelectedEvent) {
                    const modelEvent = values.SelectedEvent.Models.find((mod) => mod.ModelID === groupedModel.ModelID);
                    if (modelEvent) {
                        modelData[values.SelectedEvent.elType + 'ID'] = modelEvent[values.SelectedEvent.elType + 'ID'];
                    }
                }
                models.push(modelData);
            }
        }
        if (models.length === 0) {
            setSubmitting(false);
            notification.error({
                message: 'Error',
                description: 'Please select at least one model',
                placement: 'bottomRight',
                bottom: 100,
                duration: 0,
            });
        } else {
            apiData = {
                ...apiData,
                Contacts: [...models],
            };
            try {
                await Axios.post('/transactions/expense', apiData);
                forceReloadProject(currentProject.ID);
                forceReloadProjectAccountingDetails(currentProject.ID);
                forceReloadProjectTransactions(currentProject.ID);
                forceReloadProjectStatements(currentProject.ID);
                setSubmitting(false);
                onCloseDrawer();
            } catch (error) {
                console.error('Create Expense Error => ', error);
                setSubmitting(false);
            }
        }
    };

    const updateModelSelector = (eventselected) => {
        if (eventselected) {
            if (eventselected.elType === 'Meeting') {
                setGroupedModelDates([]);
            } else {
                setGroupedModelDates(getEventModelsForTransactions(eventselected));
            }
        } else {
            setGroupedModelDates(getProjectModelsForTransactions(currentProject));
        }
    };

    const initialValues = {
        TransactionLabelID: transactionProperties.startLabel.ID,
        Title: transactionProperties.startLabel.Label,
        ServiceID: null,
        SelectedEvent: null,
        Description: '',
        Amount: [configurations.defaultCurrency, null],
        AgencyCost: [configurations.defaultCurrency, null],
        Quantity: [1, defaultTypeQuantity],
        RefDate: currentProject.FirstDate ? getMoment(currentProject.FirstDate) : getNow(),
    };

    return currentProject ? (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
                Title: Yup.string().required('Title is required'),
                TransactionLabelID: Yup.string().required('Label is required'),
            })}
            validateOnBlur={true}
            validateOnChange={false}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(true);
                saveTransaction(values, setSubmitting);
            }}
        >
            {({ isSubmitting, values, setFieldValue, handleSubmit, touched, isValid }) => (
                <NwForm
                    values={values}
                    //onTouchForm={onChangeForm}
                    onFinish={handleSubmit}
                    layout="vertical"
                >
                    <ProjectTransactionFormContainer>
                        <Row gutter={32}>
                            <Col span={14} className="rate-form">
                                <Field
                                    component={NWNonBookingEventSelectorForTransactions}
                                    label="Event"
                                    name="SelectedEvent"
                                    events={events}
                                    onAfterChange={(value) => {
                                        if (value) {
                                            const labelSelected = values.TransactionLabelID;
                                            const transactionLabel = getTransactionLabel(labelSelected);
                                            setFieldValue('Title', `${transactionLabel.Label} for ${value.Title}`);
                                            handleChangeEventForDate(value, setFieldValue);
                                        }
                                        updateModelSelector(value);
                                    }}
                                />
                                <TransactionLabelsSelector
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    transactionProperties={transactionProperties}
                                    updateTitle
                                />
                                <Field
                                    component={NWDatePicker}
                                    label="Date"
                                    name="RefDate"
                                    format="MMM DD, YYYY"
                                    allowClear={false}
                                    value={values.RefDate}
                                />
                                <Field
                                    component={NWAutoComplete}
                                    label="Title"
                                    name="Title"
                                    value={values.Title}
                                    maxLength={255}
                                    options={[
                                        ...(transactionTitles.length > 0
                                            ? [
                                                  {
                                                      label: 'Other Transactions',
                                                      options: transactionTitles.map((title) => ({
                                                          label: title,
                                                          value: title,
                                                      })),
                                                  },
                                              ]
                                            : []),
                                        ...getProjectTitlesOptions(currentProject),
                                    ]}
                                />
                                <Field
                                    component={NWTextAreaAutoComplete}
                                    label="Description"
                                    name="Description"
                                    type="text"
                                    value={values.Description}
                                    maxLength={2500}
                                    autoSize={{ minRows: 4, maxRows: 10 }}
                                    options={[
                                        ...(transactionDescriptions.length > 0
                                            ? [
                                                  {
                                                      label: 'Other Transactions',
                                                      options: transactionDescriptions.map((description) => ({
                                                          label: description,
                                                          value: description,
                                                      })),
                                                  },
                                              ]
                                            : []),
                                        ...getProjectDescriptionsOptions(currentProject),
                                    ]}
                                />
                                <AmountLine
                                    values={values}
                                    touched={touched}
                                    fixedTypeQuantity="Units"
                                    onChangeAmount={(value) => {
                                        if (!touched.AgencyCost) {
                                            setFieldValue('AgencyCost', [value[0], value[1]]);
                                        }
                                    }}
                                    amountLabel="Expense Amount"
                                />
                                <ServiceLine values={values} setFieldValue={setFieldValue} hasAgencyCost />
                            </Col>
                            <Col span={10}>
                                <ModelSelectorProject
                                    models={groupedModelDates}
                                    updateModels={setGroupedModelDates}
                                    selectedEvent={values.SelectedEvent}
                                />
                            </Col>
                        </Row>
                    </ProjectTransactionFormContainer>
                    <NwFixedCancelAndSaveBar
                        isSubmitting={isSubmitting}
                        isValid={isValid}
                        onCancel={onCloseDrawer}
                        width={width}
                        minWidth={minWidth}
                    />
                </NwForm>
            )}
        </Formik>
    ) : (
        <Loading />
    );
};

export default ExpenseFormProject;
